import { dismissOutsideHelp } from "./help.js";
import { observeHelpHover } from "./help-hover.js";
import { observeHelpPosition, positionOpenHelp } from "./help-position.js";
import { reconcileChildren } from "./reconcile.js";
import { renderNode } from "./render-node.js";
let mountSequence = 0;
export function mountView(root, options) {
    let disposed = false;
    let rendering = false;
    let ranges;
    let view = options.project(options.source.getSnapshot());
    const renderDisposers = [];
    const dispatch = (intent) => {
        const result = options.source.dispatch(intent);
        options.onDispatchResult?.(result, intent);
        return result;
    };
    const hold = createHoldController(root.ownerDocument, dispatch);
    const context = createContext(root, options, dispatch, hold.start, renderDisposers, `e308-mount-${mountSequence++}`, () => render());
    const render = () => {
        if (disposed || rendering)
            return;
        rendering = true;
        const focus = captureFocus(root);
        captureDisclosureState(root, context.open);
        for (const dispose of renderDisposers.splice(0))
            dispose();
        reconcileChildren(root, context.renderMany(view.content), ranges.active());
        if (view.title)
            root.setAttribute("aria-label", options.resolver.text(view.title));
        else
            root.removeAttribute("aria-label");
        wireRelationships(root);
        restoreFocus(root, focus);
        const requestedFocus = context.takeFocusRequest();
        if (requestedFocus) {
            const candidate = Array.from(root.querySelectorAll("[data-e308-key]")).find((element) => element.dataset.e308Key === requestedFocus);
            candidate?.focus({ preventScroll: true });
        }
        positionOpenHelp(root);
        rendering = false;
    };
    ranges = createRangeGestureController(root, render);
    const unsubscribe = options.source.subscribe((snapshot) => {
        view = options.project(snapshot);
        render();
    });
    const keydown = (event) => {
        dismissOutsideHelp(root, event, context);
        dispatchHotkey(event, view, dispatch);
    };
    root.ownerDocument.addEventListener("keydown", keydown);
    root.ownerDocument.addEventListener("pointerup", hold.stop);
    root.ownerDocument.addEventListener("pointercancel", hold.stop);
    const stopPositioning = observeHelpPosition(root);
    const stopHelpHover = observeHelpHover(root, context.helpPreviews, (details) => {
        details.open = false;
        context.open.set(details.dataset.e308Key ?? "", false);
        details.querySelector("summary")?.setAttribute("aria-expanded", "false");
    });
    render();
    return {
        render,
        dispose() {
            if (disposed)
                return;
            disposed = true;
            unsubscribe();
            stopPositioning();
            stopHelpHover();
            root.ownerDocument.removeEventListener("keydown", keydown);
            ranges.dispose();
            root.ownerDocument.removeEventListener("pointerup", hold.stop);
            root.ownerDocument.removeEventListener("pointercancel", hold.stop);
            hold.stop();
            for (const dispose of renderDisposers.splice(0))
                dispose();
            root.replaceChildren();
        },
    };
}
function captureDisclosureState(root, open) {
    // Native details change before their queued toggle event. A synchronous input
    // dispatch or game tick must not restore the stale cached value in that gap.
    for (const details of Array.from(root.querySelectorAll("details.e308-infobox"))) {
        const id = details.dataset.e308Key;
        if (id)
            open.set(id, details.open);
    }
}
function wireRelationships(root) {
    for (const feedback of Array.from(root.querySelectorAll("[data-feedback-for]"))) {
        const targetId = feedback.dataset.feedbackFor;
        if (!targetId || !feedback.id)
            continue;
        const target = relationshipTarget(root, targetId);
        if (!target)
            continue;
        const describedBy = new Set((target.getAttribute("aria-describedby") ?? "").split(/\s+/).filter(Boolean));
        describedBy.add(feedback.id);
        target.setAttribute("aria-describedby", [...describedBy].join(" "));
    }
    for (const help of Array.from(root.querySelectorAll("[data-help-for]"))) {
        const targetId = help.dataset.helpFor;
        const content = help.querySelector(".e308-help-content");
        if (!targetId || !content?.id)
            continue;
        relationshipTarget(root, targetId)?.setAttribute("aria-details", content.id);
    }
}
function relationshipTarget(root, targetId) {
    const byId = root.ownerDocument.getElementById(targetId);
    if (byId instanceof HTMLElement && root.contains(byId))
        return byId;
    return Array.from(root.querySelectorAll("[data-action], [data-e308-key]")).find((candidate) => candidate.dataset.action === targetId || candidate.dataset.e308Key === targetId);
}
function createRangeGestureController(root, render) {
    const document = root.ownerDocument;
    let active;
    let release;
    const pointerdown = (event) => {
        if (event.target instanceof HTMLInputElement && event.target.type === "range")
            active = event.target;
    };
    const pointerup = () => {
        if (!active || !document.defaultView)
            return;
        release = document.defaultView.setTimeout(() => {
            active = undefined;
            release = undefined;
            render();
        }, 0);
    };
    const pointercancel = () => {
        active = undefined;
        render();
    };
    root.addEventListener("pointerdown", pointerdown);
    document.addEventListener("pointerup", pointerup);
    document.addEventListener("pointercancel", pointercancel);
    return {
        active: () => active,
        dispose() {
            root.removeEventListener("pointerdown", pointerdown);
            document.removeEventListener("pointerup", pointerup);
            document.removeEventListener("pointercancel", pointercancel);
            if (release !== undefined)
                document.defaultView?.clearTimeout(release);
        },
    };
}
function createContext(root, options, dispatch, startHold, renderDisposers, idPrefix, requestRender) {
    const focusedFeedback = new Set();
    const focusRequests = [];
    const context = {
        document: root.ownerDocument,
        resolver: options.resolver,
        idPrefix,
        dispatch,
        reducedMotion: options.reducedMotion ?? reducedMotion(root.ownerDocument),
        clock: options.visualClock ?? browserVisualClock(root.ownerDocument),
        overrides: options.overrides ?? {},
        open: new Map(),
        helpPreviews: new Map(),
        tabs: new Map(),
        claimed: new Set(),
        renderDisposers,
        startHold,
        requestRender,
        requestFeedbackFocus(id) {
            if (focusedFeedback.has(id))
                return;
            focusedFeedback.add(id);
            focusRequests.push(id);
        },
        clearFeedbackFocus(id) {
            focusedFeedback.delete(id);
        },
        takeFocusRequest: () => focusRequests.shift(),
        render: (node) => renderNode(node, context),
        renderMany(nodes) {
            return nodes
                .map((node) => context.render(node))
                .filter((node) => node !== undefined);
        },
    };
    return context;
}
function createHoldController(document, dispatch) {
    const window = document.defaultView;
    if (!window)
        throw new TypeError("renderer document must have a window");
    let delay;
    let repeat;
    const stop = () => {
        if (delay !== undefined)
            window.clearTimeout(delay);
        if (repeat !== undefined)
            window.clearInterval(repeat);
        delay = undefined;
        repeat = undefined;
    };
    return {
        stop,
        start(hold) {
            stop();
            delay = window.setTimeout(() => {
                dispatch(hold.intent);
                repeat = window.setInterval(() => dispatch(hold.intent), hold.repeatMs ?? 100);
            }, hold.delayMs ?? 300);
        },
    };
}
function captureFocus(root) {
    const active = root.ownerDocument.activeElement;
    if (!(active instanceof HTMLElement) || !root.contains(active))
        return undefined;
    const keyed = active.closest("[data-e308-key]");
    if (!keyed?.dataset.e308Key)
        return undefined;
    if (active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement) {
        return {
            key: active.dataset.e308Key ?? keyed.dataset.e308Key,
            start: active.selectionStart,
            end: active.selectionEnd,
        };
    }
    return { key: keyed.dataset.e308Key };
}
function restoreFocus(root, focus) {
    if (!focus)
        return;
    const candidate = Array.from(root.querySelectorAll("[data-e308-key]")).find((element) => element.dataset.e308Key === focus.key);
    if (candidate !== root.ownerDocument.activeElement)
        candidate?.focus({ preventScroll: true });
    if ((candidate instanceof HTMLInputElement || candidate instanceof HTMLTextAreaElement) &&
        focus.start !== undefined &&
        focus.start !== null) {
        candidate.setSelectionRange(focus.start, focus.end ?? focus.start);
    }
}
function dispatchHotkey(event, view, dispatch) {
    if (event.defaultPrevented || isTypingTarget(event.target))
        return;
    const scopes = new Set(view.activeScopeIds ?? []);
    const hotkey = view.hotkeys?.find((item) => hotkeyMatches(item, event, scopes));
    if (!hotkey)
        return;
    event.preventDefault();
    dispatch(hotkey.intent);
}
function hotkeyMatches(hotkey, event, scopes) {
    if (!hotkey.enabled || hotkey.key.toLowerCase() !== event.key.toLowerCase())
        return false;
    if (hotkey.scopeId && !scopes.has(hotkey.scopeId))
        return false;
    const modifiers = new Set(hotkey.modifiers ?? []);
    return (event.altKey === modifiers.has("alt") &&
        event.ctrlKey === modifiers.has("ctrl") &&
        event.metaKey === modifiers.has("meta") &&
        event.shiftKey === modifiers.has("shift"));
}
function isTypingTarget(target) {
    return (target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement ||
        (target instanceof HTMLElement && target.isContentEditable));
}
function reducedMotion(document) {
    return document.defaultView?.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
}
function browserVisualClock(document) {
    const window = document.defaultView;
    if (!window)
        throw new TypeError("renderer document must have a window");
    return {
        now: () => window.performance.now(),
        requestFrame: (callback) => window.requestAnimationFrame(callback),
        cancelFrame: (id) => window.cancelAnimationFrame(id),
    };
}
//# sourceMappingURL=mount.js.map